"use client";

import { c as _c } from "react/compiler-runtime";
import type { Offer } from "@local/offer/src/types";
import Image, { type StaticImageData } from "next/image";
import { useSearchParams } from "next/navigation";
import { useMemo, useRef } from "react";
import { hasRealDiscount } from "../../ui/CompareAtPrice";
import { Money } from "../../ui/Money";
import { PriceViewBeacon } from "@local/tracking/src/beacons/price_view";
interface HighlightPriceProps {
  market: string;
  product: Offer.PinProduct;
  prices?: Offer.ListingPrices;
  promoImage: string | StaticImageData;
  promoImageMobile?: string | StaticImageData;
  channel?: string;
  /** CTA block id of the enclosing highlight (e.g. "cta-block-11"), for segmentation. */
  callToActionId?: string;
}
export const HighlightPrice = t0 => {
  const $ = _c(48);
  const {
    market,
    product,
    prices,
    promoImage,
    promoImageMobile,
    channel,
    callToActionId
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("item_id");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const itemId = t1;
  const priceRef = useRef(null);
  const originalPrice = prices?.[0]?.value ?? product.msrp;
  const rawDiscount = prices?.[0]?.discountValue;
  const defaultDiscountPrice = rawDiscount && rawDiscount > 0 ? rawDiscount : originalPrice;
  let matchedModifierPrice;
  let hasSpecificSkuMatch = false;
  if ($[2] !== itemId || $[3] !== prices || $[4] !== product.skuLists || $[5] !== product.skus || $[6] !== product.type) {
    const allSkus = product.type === "BUNDLE" ? product.skuLists.flatMap(_temp) : product.skus;
    const matchingSku = itemId ? allSkus.find(sku => sku.legacyId === itemId || sku.attributes.some(attr => attr.name === "payloadId" && attr.value === itemId) || sku.id === itemId || sku.name === itemId) : allSkus.find(_temp2);
    if (matchingSku) {
      hasSpecificSkuMatch = true;
      if (matchingSku.name && prices?.[0]?.modifiers?.length) {
        const matchedModifier = prices[0].modifiers.find(modifier => modifier.skus.includes(matchingSku.name as string));
        if (matchedModifier) {
          matchedModifierPrice = matchedModifier.amount;
        }
      }
    }
    $[2] = itemId;
    $[3] = prices;
    $[4] = product.skuLists;
    $[5] = product.skus;
    $[6] = product.type;
    $[7] = hasSpecificSkuMatch;
    $[8] = matchedModifierPrice;
  } else {
    hasSpecificSkuMatch = $[7];
    matchedModifierPrice = $[8];
  }
  let discountPrice;
  if (hasSpecificSkuMatch) {
    discountPrice = matchedModifierPrice ?? defaultDiscountPrice;
  } else {
    let t2;
    if ($[9] !== prices) {
      t2 = prices?.[0]?.modifiers?.length ? prices[0].modifiers.reduce(_temp3, prices[0].modifiers[0].amount) : undefined;
      $[9] = prices;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    const lowestModifierPrice = t2;
    discountPrice = lowestModifierPrice && lowestModifierPrice < defaultDiscountPrice ? lowestModifierPrice : defaultDiscountPrice;
  }
  let t2;
  if ($[11] !== discountPrice || $[12] !== originalPrice) {
    t2 = Math.round((originalPrice - discountPrice) / originalPrice * 100);
    $[11] = discountPrice;
    $[12] = originalPrice;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const discountPercentage = t2;
  let t3;
  if ($[14] !== discountPercentage || $[15] !== discountPrice || $[16] !== originalPrice) {
    t3 = {
      originalPrice,
      discountPrice,
      discountPercentage
    };
    $[14] = discountPercentage;
    $[15] = discountPrice;
    $[16] = originalPrice;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const priceData = t3;
  let t4;
  if ($[18] !== priceData.discountPrice || $[19] !== priceData.originalPrice) {
    t4 = hasRealDiscount(priceData.originalPrice, priceData.discountPrice);
    $[18] = priceData.discountPrice;
    $[19] = priceData.originalPrice;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const discounted = t4;
  let t5;
  if ($[21] !== promoImage || $[22] !== promoImageMobile) {
    t5 = <div className="mb-4 flex justify-center">{promoImageMobile ? <><Image alt="Promo" height={100} src={promoImage} width={400} className="hidden w-full md:block" /><Image alt="Promo" height={100} src={promoImageMobile} width={400} className="w-full md:hidden" /></> : <Image alt="Promo" height={100} src={promoImage} width={400} className="w-full" />}</div>;
    $[21] = promoImage;
    $[22] = promoImageMobile;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== discounted || $[25] !== market || $[26] !== priceData.originalPrice) {
    t6 = discounted && <p className="text-base font-normal text-gray-600 line-through"><Money value={priceData.originalPrice} market={market} srLabel="Original price" /></p>;
    $[24] = discounted;
    $[25] = market;
    $[26] = priceData.originalPrice;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const t7 = discounted ? "Sale price" : "Price";
  let t8;
  if ($[28] !== market || $[29] !== priceData.discountPrice || $[30] !== t7) {
    t8 = <p ref={priceRef} className="font-display text-5xl font-semibold text-green-700 uppercase"><Money value={priceData.discountPrice} market={market} srLabel={t7} /></p>;
    $[28] = market;
    $[29] = priceData.discountPrice;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== callToActionId || $[33] !== channel || $[34] !== market || $[35] !== priceData.discountPrice) {
    t9 = <PriceViewBeacon targetRef={priceRef} market={market} priceInCents={priceData.discountPrice} channel={channel} ctaId={callToActionId} />;
    $[32] = callToActionId;
    $[33] = channel;
    $[34] = market;
    $[35] = priceData.discountPrice;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== discounted || $[38] !== priceData.discountPercentage) {
    t10 = discounted && <p className="text-xl font-semibold text-green-700 uppercase">Save {priceData.discountPercentage}%</p>;
    $[37] = discounted;
    $[38] = priceData.discountPercentage;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t6 || $[42] !== t8 || $[43] !== t9) {
    t11 = <div className="my-4 text-center">{t6}{t8}{t9}{t10}</div>;
    $[40] = t10;
    $[41] = t6;
    $[42] = t8;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t11 || $[46] !== t5) {
    t12 = <div className="relative">{t5}{t11}</div>;
    $[45] = t11;
    $[46] = t5;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  return t12;
};
function _temp(skuList) {
  return skuList.skus;
}
function _temp2(sku_0) {
  return sku_0.isPreselected === true;
}
function _temp3(min, m) {
  return m.amount < min ? m.amount : min;
}